import Add from "@mui/icons-material/Add";
import Send from "@mui/icons-material/Send";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useEffect, useRef, useState, type FC, type FocusEvent } from "react";
import { useWatch } from "react-hook-form";
import { CeTextInput } from "../generic/components/form-fields/ce-text-field";
import type { CreateContentRequest } from "../multiformat/store/types";
import { CreateContentFileUpload } from "./create-content-file-upload";

export const ContentDescriptionTextField: FC<{
  format?: string;
  fileUpload: boolean;
}> = ({ format, fileUpload }) => {
  const [inputTouched, setInputTouched] = useState(false);
  const skipBlur = useRef(false);
  const [q, file] = useWatch<CreateContentRequest>({ name: ["q", "file"] });
  const inputLabel = format ? `Describe your ${format}` : "";

  const markDirty = (): void => {
    if (!inputTouched) setInputTouched(true);
  };

  useEffect(() => {
    if (q && !inputTouched) {
      queueMicrotask(() => {
        setInputTouched(true);
      });
    }
  }, [inputTouched, q]);

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (skipBlur.current) {
      skipBlur.current = false;
      return;
    }
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;
    if (!q && !file) setInputTouched(false);
  };

  return (
    <Box sx={{ position: "relative", p: 0, m: 0, backgroundColor: "white", borderRadius: 2 }}>
      <CeTextInput<CreateContentRequest>
        variant="outlined"
        name="q"
        label={inputTouched ? inputLabel : undefined}
        placeholder={!inputTouched ? inputLabel : undefined}
        multiline
        fullWidth
        minRows={inputTouched ? 4 : 1}
        maxRows={10}
        onFocus={markDirty}
        onClick={markDirty}
        onChange={markDirty}
        onBlur={handleBlur}
        slotProps={{
          input: {
            startAdornment: (
              <>
                {!inputTouched && fileUpload && (
                  <Tooltip title="Upload a file">
                    <Add sx={{ color: "text.secondary", cursor: "pointer", ml: -0.5 }} />
                  </Tooltip>
                )}
              </>
            ),
            endAdornment: (
              <>
                {!inputTouched && (
                  <IconButton
                    data-analytics-id="create-content-generate-disabled"
                    color="primary"
                    disabled={true}
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                    }}
                  >
                    <Send />
                  </IconButton>
                )}
                {inputTouched && fileUpload && (
                  <Box onMouseDownCapture={() => (skipBlur.current = true)}>
                    <CreateContentFileUpload />
                  </Box>
                )}
                {inputTouched && (
                  <IconButton
                    data-analytics-id="create-content-generate"
                    data-testid="submit-button"
                    color="primary"
                    type="submit"
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                    }}
                  >
                    <Send />
                  </IconButton>
                )}
              </>
            ),
            sx: {
              position: "relative",
              alignItems: inputTouched ? "start" : "center",
              pb: inputTouched ? "2.5rem" : 1.5,
              "& textarea": {
                pl: !inputTouched && fileUpload ? 1 : 0,
                transition: "height 300ms ease",
              },
            },
          },
        }}
      />
    </Box>
  );
};
